import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import HomePage from "@/pages/HomePage";
import LocationPage from "@/pages/LocationPage";
import FuehrerscheinPage from "@/pages/FuehrerscheinKlasseB";
import KostenrechnerPage from "@/pages/KostenrechnerPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import ContactPage from "@/pages/ContactPage";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import TheorieKalenderPage from "@/pages/TheorieKalenderPage";
import IntensivkursPage from "@/pages/IntensivkursPage";
import IntensivkursSeite from "@/pages/IntensivkursSeite";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLocations from "@/pages/admin/AdminLocations";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSlider from "@/pages/admin/AdminSlider";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminLicenseClasses from "@/pages/admin/AdminLicenseClasses";
import AdminPages from "@/pages/admin/AdminPages";
import PageBuilder from "@/pages/admin/PageBuilder";
import AdminMenus from "@/pages/admin/AdminMenus";
import AdminContent from "@/pages/admin/AdminContent";
import AdminUsers from "@/pages/admin/AdminUsers";

// Dynamic CMS Page
import DynamicPage from "@/pages/DynamicPage";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdminLayout from "@/components/AdminLayout";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";

// Hooks
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

export const useAuth = () => React.useContext(AuthContext);

function App() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setAdmin({ ...res.data, token });
      }).catch(() => {
        localStorage.removeItem("adminToken");
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    
    // Seed database on first load
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("adminToken", res.data.token);
      setAdmin(res.data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || "Login fehlgeschlagen" };
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    setAdmin(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ admin, login, logout }}>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <CookieConsent />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/standorte/:slug" element={<PublicLayout><LocationPage /></PublicLayout>} />
          <Route path="/fuehrerschein" element={<PublicLayout><FuehrerscheinPage /></PublicLayout>} />
          <Route path="/fuehrerschein-klasse-b" element={<Navigate to="/fuehrerschein" replace />} />
          <Route path="/kostenrechner" element={<Navigate to="/fuehrerschein" replace />} />
          <Route path="/intensivkurs" element={<PublicLayout><IntensivkursPage /></PublicLayout>} />
          <Route path="/fuehrerschein-7-tage" element={<PublicLayout><IntensivkursSeite /></PublicLayout>} />
          <Route path="/intensivkurs-fuehrerschein" element={<Navigate to="/fuehrerschein-7-tage" replace />} />
          <Route path="/blog" element={<PublicLayout><BlogListPage /></PublicLayout>} />
          <Route path="/blog/:slug" element={<PublicLayout><BlogArticlePage /></PublicLayout>} />
          <Route path="/kontakt" element={<PublicLayout><ContactPage /></PublicLayout>} />
          <Route path="/theorie-kalender" element={<PublicLayout><TheorieKalenderPage /></PublicLayout>} />
          <Route path="/kalender" element={<Navigate to="/theorie-kalender" replace />} />
          <Route path="/impressum" element={<PublicLayout><ImpressumPage /></PublicLayout>} />
          <Route path="/datenschutz" element={<PublicLayout><DatenschutzPage /></PublicLayout>} />
          
          {/* Dynamic CMS Pages */}
          <Route path="/seite/:slug" element={<PublicLayout><DynamicPage /></PublicLayout>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/slider" element={<AdminLayout><AdminSlider /></AdminLayout>} />
          <Route path="/admin/standorte" element={<AdminLayout><AdminLocations /></AdminLayout>} />
          <Route path="/admin/blog" element={<AdminLayout><AdminBlog /></AdminLayout>} />
          <Route path="/admin/bewertungen" element={<AdminLayout><AdminTestimonials /></AdminLayout>} />
          <Route path="/admin/anfragen" element={<AdminLayout><AdminContacts /></AdminLayout>} />
          <Route path="/admin/kalender" element={<AdminLayout><AdminCalendar /></AdminLayout>} />
          <Route path="/admin/preise" element={<AdminLayout><AdminPricing /></AdminLayout>} />
          <Route path="/admin/fuehrerscheinklassen" element={<AdminLayout><AdminLicenseClasses /></AdminLayout>} />
          <Route path="/admin/seiten" element={<AdminLayout><AdminPages /></AdminLayout>} />
          <Route path="/admin/seiten/:slug" element={<AdminLayout><PageBuilder /></AdminLayout>} />
          <Route path="/admin/menus" element={<AdminLayout><AdminMenus /></AdminLayout>} />
          <Route path="/admin/inhalte" element={<AdminLayout><AdminContent /></AdminLayout>} />
          <Route path="/admin/benutzer" element={<AdminLayout><AdminUsers /></AdminLayout>} />
          <Route path="/admin/einstellungen" element={<AdminLayout><AdminSettings /></AdminLayout>} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

const PublicLayout = ({ children }) => {
  useGoogleAnalytics(); // Track page views
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default App;
